'use client';

import { ReactNode } from 'react';
import { useCandidateNavigationOptional, type CandidateSearchFilters } from '@components/contexts/CandidateNavigationContext';
import { cn } from '@utils/cn';
import { PromptBarShell } from './PromptBarShell';
import { PromptBarScrim } from './PromptBarScrim';
import { PromptBarActionsPopup } from './PromptBarActionsPopup';
import { SearchBranch } from './SearchBranch';
import { ChatBranch } from './ChatBranch';
import { usePromptBarMode, type PromptBarMode } from './usePromptBarMode';

interface CandidatePromptBarProps {
  locale: Locale;
  isAuthenticated?: boolean;
  /** Force a mode. Otherwise derived from currentView via usePromptBarMode. */
  modeOverride?: PromptBarMode;
  /** Chat submit handler. Required for chat mode to function; if absent the
   *  branch still renders but the user cannot send a message. */
  onChatSubmit?: (text: string) => Promise<void>;
  /** Search submit handler. Defaults to navigateToCandidateSearch. */
  onSearchSubmit?: (filters: CandidateSearchFilters) => void;
  /** Render in document flow instead of fixed-position overlay. */
  inline?: boolean;
  /** Pin to the viewport bottom while scrolling, then dock in-flow at the end of the
   *  page. Wrapper is `sticky`; the shell renders in-flow. */
  sticky?: boolean;
  /** Extra content rendered under the bar (e.g. a legal bar). Pins/docks together with it. */
  belowBarSlot?: ReactNode;
  /** Rendered after the mic/send button in chat mode — used by the chat page for the history
   *  button. Ignored in search mode; `ChatBranch` hides it while the visitor is typing. */
  chatRightSlot?: ReactNode;
}

const noopChatSubmit: (text: string) => Promise<void> = async () => {};

export const CandidatePromptBar = ({
  locale,
  isAuthenticated = false,
  modeOverride,
  onChatSubmit,
  onSearchSubmit,
  inline = false,
  sticky = false,
  belowBarSlot,
  chatRightSlot,
}: CandidatePromptBarProps) => {
  const mode = usePromptBarMode(modeOverride);
  const candidateNav = useCandidateNavigationOptional();
  const handleSearchSubmit = onSearchSubmit ?? candidateNav?.navigateToCandidateSearch;

  const popup = <PromptBarActionsPopup locale={locale} mode={mode} isAuthenticated={isAuthenticated} />;

  return (
    <>
      {/* Sticky mode uses the in-flow shell (no built-in scrim), so paint the scrim here. */}
      {sticky && <PromptBarScrim />}
      <div className={cn('w-full', sticky && 'sticky bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 px-1 lg:bottom-8 lg:px-2')}>
        <PromptBarShell
          inline={inline || sticky}
          belowSlot={belowBarSlot}
        >
          {mode === 'search' && handleSearchSubmit
            ? <SearchBranch locale={locale} onSubmit={handleSearchSubmit} leftSlot={popup} />
            : (
              <ChatBranch
                onSubmit={onChatSubmit ?? noopChatSubmit}
                // In chat mode the `+` is always the attach button, signed in or not — those
                // navigation destinations live in the mobile BottomNav and the desktop header, and
                // swapping the control's meaning on auth state made it vanish mid-conversation when
                // a session lapsed. Attaching needs an account, so a signed-out visitor is sent to
                // login with a path back here, rather than shown a different button.
                rightSlot={chatRightSlot}
                allowAttachments
              />
            )}
        </PromptBarShell>
      </div>
    </>
  );
};
